import { useState, useEffect, useRef } from "react";
import axios from "axios";

export default function EditorPanel({
  jobId,
  pageCount,
  content,
  outputFormat,
  filename,
  onClose,
}) {
  const [text, setText] = useState(content);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages] = useState(pageCount || 1);
  const [issues, setIssues] = useState([]);
  const [jumpLine, setJumpLine] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    axios
      .post("/api/detect-issues", { content: text })
      .then((res) => setIssues(res.data.issues))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (jumpLine && textareaRef.current) {
      const lines = text.split("\n");
      const pos = lines.slice(0, jumpLine - 1).join("\n").length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(pos, pos);
      textareaRef.current.scrollTop =
        (jumpLine / lines.length) * textareaRef.current.scrollHeight;
    }
  }, [jumpLine]);

  const handleDownload = () => {
    const ext = outputFormat === "latex" ? ".tex" : ".docx";
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename.replace(/\.[^.]+$/, ext);
    a.click();
    URL.revokeObjectURL(url);
  };

  const errorCount = issues.filter((i) => i.type === "error").length;
  const warnCount = issues.filter((i) => i.type === "warning").length;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        padding: "16px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "12px",
          color: "white",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "15px" }}>
          Review &amp; Edit Output
        </span>
        {errorCount > 0 && (
          <span
            style={{
              background: "#dc2626",
              padding: "2px 8px",
              borderRadius: "6px",
              fontSize: "12px",
            }}
          >
            {errorCount} error{errorCount !== 1 ? "s" : ""}
          </span>
        )}
        {warnCount > 0 && (
          <span
            style={{
              background: "#d97706",
              padding: "2px 8px",
              borderRadius: "6px",
              fontSize: "12px",
            }}
          >
            {warnCount} warning{warnCount !== 1 ? "s" : ""}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          <button
            onClick={handleDownload}
            style={{
              background: "#16a34a",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "13px",
              fontFamily: "Tajawal, sans-serif",
            }}
          >
            Download {outputFormat === "latex" ? ".tex" : ".docx"}
          </button>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.08)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.2)",
              padding: "8px 16px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "13px",
              fontFamily: "Tajawal, sans-serif",
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Main panels */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: jobId ? "1fr 1fr" : "1fr",
          gap: "12px",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left: PDF viewer — only shown when jobId is available */}
        {jobId && (
          <div
            style={{
              background: "#1e1e1e",
              borderRadius: "12px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "8px 14px",
                borderBottom: "1px solid #333",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                color: "#aaa",
              }}
            >
              <span>Original PDF</span>
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                <button
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  style={{
                    background: "transparent",
                    border: "1px solid #444",
                    color: currentPage === 0 ? "#555" : "#aaa",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    cursor: currentPage === 0 ? "default" : "pointer",
                  }}
                >
                  ‹
                </button>
                <span style={{ fontSize: "12px" }}>
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  disabled={currentPage >= totalPages - 1}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  style={{
                    background: "transparent",
                    border: "1px solid #444",
                    color: currentPage >= totalPages - 1 ? "#555" : "#aaa",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    cursor:
                      currentPage >= totalPages - 1 ? "default" : "pointer",
                  }}
                >
                  ›
                </button>
              </div>
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                padding: "12px",
                background: "#2a2a2a",
              }}
            >
              <img
                src={`/api/pdf-page/${jobId}/${currentPage}`}
                style={{
                  maxWidth: "100%",
                  borderRadius: "4px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                }}
                alt={`Page ${currentPage + 1}`}
              />
            </div>
          </div>
        )}

        {/* Right: Text editor */}
        <div
          style={{
            background: "#1e1e1e",
            borderRadius: "12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: "8px 14px",
              borderBottom: "1px solid #333",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "13px",
              color: "#aaa",
            }}
          >
            <span>Converted Output</span>
            <span style={{ fontSize: "11px", color: "#555" }}>(editable)</span>
          </div>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{
              flex: 1,
              background: "#1e1e1e",
              color: "#d4d4d4",
              border: "none",
              padding: "16px",
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontSize: "12px",
              lineHeight: "1.6",
              resize: "none",
              outline: "none",
            }}
            spellCheck={false}
          />
        </div>
      </div>

      {/* Issues bar */}
      {issues.length > 0 && (
        <div
          style={{
            marginTop: "10px",
            background: "#1e1e1e",
            borderRadius: "8px",
            padding: "10px 14px",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span
            style={{ fontSize: "12px", color: "#666", marginRight: "4px" }}
          >
            Jump to issue:
          </span>
          {issues.slice(0, 8).map((issue, i) => (
            <button
              key={i}
              onClick={() => setJumpLine(issue.line)}
              style={{
                background:
                  issue.type === "error"
                    ? "rgba(220,38,38,0.2)"
                    : "rgba(217,119,6,0.2)",
                border: `1px solid ${
                  issue.type === "error" ? "#dc2626" : "#d97706"
                }`,
                color: issue.type === "error" ? "#fca5a5" : "#fcd34d",
                padding: "3px 10px",
                borderRadius: "6px",
                fontSize: "11px",
                cursor: "pointer",
                fontFamily: "Tajawal, sans-serif",
              }}
            >
              Line {issue.line} — {issue.message}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
